import React, { useEffect, useState } from "react";
import ApiTokenDataGrid from "./ApiTokenDataGrid";
import { Alert, Box, Button, Stack } from "@mui/material";
import { useTranslation } from 'react-i18next';

import CreateApiToken from "./CreateApiToken";
import ApiTokenPreview from "./ApiTokenPreview";
import { InfegyUser } from "system/InfegyUser";
import APIRequest from "system/APIRequest";

// The APITokenPreview is what is returned from the API on creation.  Some fields such as 
// `access_token` do not exist when requesting API Tokens later on (for security reasons).
export interface ApiTokenPreview {
  access_token: string;
  created_at: string; // datetime
  description: string;
  expires_at: string; // datetime
  expires_in: number; // (in seconds)
  token_type: string; // "Bearer"
  zone: string; // "api"
}

// The ApiToken is what is returned when getting a list of API Tokens from the API.
export interface ApiToken {
  _deleted: boolean;
  authenticator: string; // "password"
  created_at: string; // datetime
  deleted_at: string; // datetime
  description: string;
  expires_at: string; // datetime
  id: string;
  remote_address: string;
  type: string; // "api"
  updated_at: string; // datetime
  user_id: string;
}

interface ApiTokensProps {
  /**
   * The InfegyUser to manage API Tokens for.
   */
  user: InfegyUser;
}

export default function ApiTokens(props: ApiTokensProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [apiTokens, setApiTokens] = useState<Array<ApiToken>>([]);

  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [apiTokenPreview, setApiTokenPreview] = useState<ApiTokenPreview | null>(null);
  const [isPreviewingToken, setIsPreviewingToken] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  async function handleRevoke(apiToken: ApiToken): Promise<void> {
    setApiTokens(apiTokens.filter((item) => item.id !== apiToken.id));
  }

  function handleRevokeError(apiToken: ApiToken, message: string): void {
    setErrorMessage(`${i18n.t("manage-api-tokens.revoke-token-error")} ${apiToken.description}. ${message}`);
  }

  function handleAlertClose(): void {
    setErrorMessage("");
  }

  function handleCreateToken(): void {
    setIsCreatingToken(true);
  }

  function handleCreateApiTokenClose(): void {
    setIsCreatingToken(false);
  }

  function handleCreateApiTokenSuccess(apiToken: ApiTokenPreview): void {
    setApiTokenPreview(apiToken);
    setIsCreatingToken(false);
    setIsPreviewingToken(true);
    fetchData();
  }

  function handleTokenPreviewClose(): void {
    setIsPreviewingToken(false);
    setApiTokenPreview(null);
  }

  async function fetchData(): Promise<void> {
    try {
      const response = await APIRequest("obj/auth-token", {type: "api"});
      const apiTokens: Array<ApiToken> = response.output;
      setApiTokens(apiTokens);
    } 
    catch (e: any) {
      setErrorMessage(e.server_message);
    }
  }

  useEffect(() => {
    fetchData();
  }, [])

  return (
    <>
      <Stack spacing={2} direction="row" sx={{
        marginBottom: 1
      }}>
        <Box sx={{
          flex: 1
        }}>{i18n.t("manage-api-tokens.description")}</Box>
        <Box>
          <Button variant="contained" onClick={handleCreateToken}>{i18n.t("manage-api-tokens.create-button")}</Button>
        </Box>
      </Stack>
      <Stack spacing={2}>
        {errorMessage && <Alert variant="filled" severity="error" onClose={handleAlertClose}>{errorMessage}</Alert>}
        {apiTokens.length > 0 && <ApiTokenDataGrid apiTokens={apiTokens} onRevoke={handleRevoke} onRevokeError={handleRevokeError} />}
      </Stack>
      {isCreatingToken &&
        <CreateApiToken isOpen={isCreatingToken} user={props.user} onClose={handleCreateApiTokenClose} onSuccess={handleCreateApiTokenSuccess} />
      }
      <ApiTokenPreview isOpen={isPreviewingToken} apiTokenPreview={apiTokenPreview} onClose={handleTokenPreviewClose}/>
    </>
  );
}